import Notification from '../Components/Layout/Notification';

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface RoleFormData {
  name: string;
}

interface TenantFormData {
  name: string;
  tenantKey: string;
}

interface TenantRef {
  name?: string;
}

interface UserRef {
  login?: string;
  [key: string]: unknown;
}

const showErrorAndReturnFalse = (message: string): false => {
  Notification.showError(message);
  return false;
};

const isFieldEmpty = (value: string, fieldName: string): boolean => {
  return value.trim() === '' ? showErrorAndReturnFalse(`Please enter ${fieldName}!`) : true;
};

const isTenantPresent = (tenant: TenantRef | null | undefined, fieldName: string): boolean => {
  return !tenant || !tenant.name || tenant.name.trim() === ''
    ? showErrorAndReturnFalse(`${fieldName} is a required field.`)
    : true;
};

const isFieldLengthValid = (value: string, maxLength: number, fieldName: string): boolean => {
  return value.length > maxLength
    ? showErrorAndReturnFalse(`${fieldName} must not exceed ${maxLength} characters.`)
    : true;
};

const validateRoleData = (formData: RoleFormData): boolean => {
  return (
    isFieldEmpty(formData.name, 'Role Name') && isFieldLengthValid(formData.name, 50, 'Role Name')
  );
};

const validateTenantData = (formData: TenantFormData): boolean => {
  return (
    isFieldEmpty(formData.name, 'Tenant Name') &&
    isFieldLengthValid(formData.name, 50, 'Tenant Name') &&
    isFieldEmpty(formData.tenantKey, 'Tenant Key') &&
    isFieldLengthValid(formData.tenantKey, 50, 'Tenant Key')
  );
};

const validateUserData = (
  user: UserRef | null | undefined,
  formData: UserFormData,
  tenant: TenantRef | null | undefined,
  password: string | null | undefined
): boolean => {
  const isNameValid = (fieldName: string, value: string): boolean =>
    isFieldEmpty(value, fieldName) && isFieldLengthValid(value, 50, fieldName);

  const isEmailValid = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      ? true
      : showErrorAndReturnFalse('Please enter a valid Email Address, e.g., user@domain.com');

  const isPasswordValid = (): boolean => {
    if (!user && !password) {
      return showErrorAndReturnFalse('Password is a required field.');
    }

    if (password) {
      if (!doesPasswordContainUserInfo(password, formData)) {
        return showErrorAndReturnFalse(
          "Password can't contain username, email, first or last name."
        );
      }

      if (!isPasswordComplexEnough(password)) {
        return showErrorAndReturnFalse(
          'Password must contain minimum 12 characters and at least one lowercase letter, one uppercase letter, one special character, and one number.'
        );
      }
    }

    return true;
  };

  return (
    isNameValid('First Name', formData.firstName) &&
    isNameValid('Last Name', formData.lastName) &&
    isFieldEmpty(formData.email, 'Email Address') &&
    isEmailValid(formData.email) &&
    isPasswordValid() &&
    isTenantPresent(tenant, 'Tenant')
  );
};

const doesPasswordContainUserInfo = (password: string, formData: UserFormData): boolean => {
  const containsUserInformation =
    password.includes(formData.firstName) ||
    password.includes(formData.lastName) ||
    password.includes(formData.email);
  return !containsUserInformation;
};

const isPasswordComplexEnough = (password: string): boolean => {
  const isLengthValid = password.length >= 12;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_\-+=<>?]/.test(password);

  return isLengthValid && hasLowercase && hasUppercase && hasDigit && hasSpecial;
};

export { validateRoleData, validateTenantData, validateUserData };
